<template>
  <div class="landing-page">
    <header class="lp-header">
      <div class="lp-container header-inner">
          <div class="brand">
            <img :src="brandLogo" alt="MOH" class="brand-mark"/>
            <div class="brand-name">iHRIS-HRD</div>
          </div>

        <nav class="nav-links">
          <button
            v-for="link in navLinks"
            :key="link.target"
            class="link-button"
            :class="{'is-active': link.target === 'elms-guide' ? showElmsGuide : !showElmsGuide && activeSection === link.target}"
            :aria-haspopup="link.target === 'elms-guide' ? 'dialog' : null"
            :aria-expanded="link.target === 'elms-guide' ? String(showElmsGuide) : null"
            :aria-controls="link.target === 'elms-guide' ? 'elms-guide' : null"
            @click="handleNav(link.target, $event)"
          >
            {{ link.label }}
          </button>
        </nav>

        <button class="menu-button" aria-label="Open navigation" @click="mobileNavOpen = true">
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      <transition name="fade">
        <div v-if="mobileNavOpen" class="mobile-overlay" @click.self="mobileNavOpen = false">
          <div class="mobile-sheet">
            <div class="mobile-header">
              <div class="brand">
                <img :src="brandLogo" alt="MOH" class="brand-mark"/>
                <div class="brand-name">iHRIS-HRD</div>
              </div>
              <button class="close-button" @click="mobileNavOpen = false">X</button>
            </div>
            <nav class="mobile-links">
              <button
                v-for="link in navLinks"
                :key="link.target"
                class="mobile-link"
                :aria-haspopup="link.target === 'elms-guide' ? 'dialog' : null"
                @click="handleMobileNav(link.target, $event)"
              >
                {{ link.label }}
              </button>
            </nav>
          </div>
        </div>
      </transition>
    </header>

    <main>
      <section id="hero" class="lp-section hero">
        <div class="lp-container hero-grid">
          <div class="hero-copy">
            <p class="eyebrow">Human Resource Development</p>
            <h1 class="hero-title">
              iHRIS-HRD<br>
              <span class="text-accent">Human Resource Development</span>
            </h1>
            <p class="lead">
              Manage graduates, short and long term trainings, programs, trainees, and trainers in one place. With each
              record tied to the person and program, you can see workforce capacity at any time—without digging through
              spreadsheets.
            </p>
            <div class="hero-actions">
              <button class="btn primary" @click="goToLogin">Sign in</button>
            </div>
            <p class="tiny-note">
              Built for MOH teams that prioritize accurate HR data, clear workflows, and actionable development insights.
            </p>
          </div>

        </div>
      </section>

      <section id="insights" class="lp-section alt">
        <div class="lp-container">
          <div class="insights-header">
            <div>
              <p class="eyebrow muted">Public snapshot</p>
              <h2 class="section-title">HRD momentum at a glance</h2>
              <p class="section-lead narrow">
                High-level workforce and training signals surfaced for decision makers—kept simple for a quick read.
              </p>
            </div>
          </div>

          <div class="insight-wrap">
            <div class="insight-mini-grid public-kpi-grid">
              <div v-for="metric in publicKpis" :key="metric.title" class="mini-card public-kpi-card">
                <div class="mini-top">
                  <span class="mini-title">{{ metric.title }}</span>
                </div>
                <div class="mini-value">{{ formatNumber(metric.value) }}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="product" class="lp-section">
        <div class="lp-container">
          <p class="eyebrow muted">Products</p>
          <h2 class="section-title">Human Resource Development (HRD)</h2>
          <p class="section-lead">
            Designed to manage the health sector workforce across all levels of the health system—Ministry, Regional,
            Hospital, Zonal, and Woreda—this system enables decision-makers to access reliable evidence for informed
            policy and staffing decisions. It supports comprehensive management of graduates, short- and long-term
            trainings, programs, trainees, and trainers, ensuring training records remain linked to the appropriate
            individuals and facilities. As a result, workforce capacity and training coverage can be easily tracked and
            understood at a glance.
          </p>
          <div class="card-grid three-up">
            <div
              v-for="highlight in productHighlights"
              :key="highlight.title"
              class="info-card"
            >
              <h3>{{ highlight.title }}</h3>
              <p>{{ highlight.body }}</p>
            </div>
          </div>

          <div class="timeline">
            <div class="timeline-header">
              <p class="eyebrow muted">Rollout path</p>
              <p class="timeline-caption">Built to follow existing HR workflows, step by step.</p>
            </div>
            <div class="timeline-steps">
              <div
                v-for="step in steps"
                :key="step.title"
                class="timeline-step"
              >
                <div class="timeline-badge">{{ step.step }}</div>
                <div>
                  <div class="timeline-title">{{ step.title }}</div>
                  <div class="timeline-text">{{ step.body }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="hr-roles" class="lp-section">
        <div class="lp-container">
          <p class="eyebrow muted">For every HR lead</p>
          <h2 class="section-title">Built for the whole HR ecosystem</h2>
          <p class="section-lead narrow">
            iHRIS-HRD supports ministry, regional, hospital, zonal, and woreda teams with one consistent, simple view of the workforce.
          </p>
          <div class="card-grid four-up">
            <div
              v-for="audience in clinicians"
              :key="audience.title"
              class="info-card"
            >
              <h3>{{ audience.title }}</h3>
              <p>{{ audience.body }}</p>
            </div>
          </div>
        </div>
      </section>

    </main>

    <footer class="lp-footer">
      <div class="lp-container footer-content">
        <div class="footer-meta">
          <div>(c) {{ currentYear }} MOH iHRIS-HRD.</div>
          <div class="footer-subtitle">Simple. Human. Stewarded by the Ministry of Health.</div>
        </div>
        <div class="footer-links">
          <div class="footer-link" v-for="link in footerLinks" :key="link.label">
            <a :href="link.href">{{ link.label }}</a>
          </div>
          <div class="footer-subtitle">Need to talk compliance? Email us anytime.</div>
        </div>
      </div>
    </footer>

    <transition name="fade-slide">
      <div v-if="showElmsGuide" id="elms-guide" ref="guideDialog" class="elms-modal" role="dialog" aria-modal="true" aria-labelledby="elms-guide-title" aria-describedby="elms-guide-description" @keydown="handleGuideKeydown">
        <div class="elms-modal__backdrop" @click="closeElmsGuide"></div>
        <div class="elms-guide" role="document">
          <header class="elms-guide-header">
            <div><span class="elms-eyebrow">MOODLE RESOURCES <span>API v1</span></span><h2 id="elms-guide-title">eLMS integrations</h2><p id="elms-guide-description">Choose the Moodle plugin or a direct API connection to send learning records to HRD.</p></div>
            <button ref="guideClose" class="elms-close" type="button" aria-label="Close integration guide" @click="closeElmsGuide"><v-icon size="20">mdi-close</v-icon></button>
          </header>
          <div class="elms-method-picker">
            <span class="elms-nav-label">TWO WAYS TO CONNECT</span>
            <div class="elms-method-tabs" role="tablist" aria-label="Integration method">
              <button v-for="method in integrationMethods" :id="'elms-method-' + method.id" :key="method.id" type="button" role="tab" :aria-label="method.label" :aria-selected="String(integrationMethod === method.id)" aria-controls="elms-method-panel" :tabindex="integrationMethod === method.id ? 0 : -1" @click="selectIntegrationMethod(method.id)" @keydown="handleIntegrationMethodKeydown($event, method.id)">
                <v-icon size="21">{{ method.icon }}</v-icon><span><strong>{{ method.label }}</strong><small>{{ method.description }}</small></span><v-icon v-if="integrationMethod === method.id" class="elms-method-check" size="18">mdi-check-circle</v-icon>
              </button>
            </div>
          </div>
          <div id="elms-method-panel" class="elms-doc-layout" role="tabpanel" :aria-labelledby="'elms-method-' + integrationMethod">
            <nav ref="guideNav" class="elms-doc-nav" aria-label="Integration guide sections"><span class="elms-nav-label">IN THIS GUIDE</span><button v-for="(section, index) in guideSections" :key="section.id" type="button" :class="{'is-current':activeGuideSection === section.id}" :aria-current="activeGuideSection === section.id ? 'location' : null" @click="scrollToGuide(section.id)"><span>{{ String(index + 1).padStart(2, '0') }}</span>{{ section.label }}</button><div class="elms-nav-note"><v-icon small color="#64748b">mdi-server-outline</v-icon>{{ integrationMethod === 'moodle' ? 'For Moodle site administrators' : 'For developers building a server-side connector' }}</div></nav>
            <div :key="integrationMethod" ref="guideContent" class="elms-doc-content" tabindex="0" aria-label="Integration documentation" @scroll.passive="syncGuideSection">
              <section id="elms-overview" class="elms-doc-section" aria-labelledby="elms-overview-title">
                <span class="elms-section-number">01 / OVERVIEW</span><h3 id="elms-overview-title">From learning to workforce records</h3>
                <p v-if="integrationMethod === 'moodle'">Install <code>local_hrdintegration</code> on your Moodle site. The plugin sends completions and certificate evidence to HRD, manages course mappings, and handles background delivery and retries. Follow this path to configure an existing Moodle site.</p>
                <p v-else>Build a server-side connector that sends completion and certificate evidence directly to HRD's REST API. Your team implements authentication, payload creation, delivery, receipt storage and retries. Follow this path when you want to manage the connection in your own application.</p>
                <p>Both methods use the same HRD API, source registration and accredited course mappings. HRD validates the learner profile, matches or registers a trainee, and stores the completion receipt.</p>
                <div class="elms-flow" aria-label="Integration flow"><span><v-icon small>mdi-school-outline</v-icon>Course completed</span><b aria-hidden="true">+</b><span><v-icon small>mdi-certificate-outline</v-icon>Certificate issued</span><b aria-hidden="true">→</b><span><v-icon small>mdi-check-circle-outline</v-icon>HRD completion</span></div>
                <div class="elms-base-card"><div><span class="elms-nav-label">HRD API BASE URL</span><code>{{ hrdApiBase }}</code></div><button class="elms-copy-btn" type="button" aria-label="Copy HRD base URL" @click="copyToClipboard(hrdApiBase)"><v-icon small>mdi-content-copy</v-icon>Copy</button></div>
                <div class="elms-callout"><strong>Automatic matching</strong><p>HRD matches phone, personal email or supplied identity numbers. If several trainees match, HRD selects the smallest trainee ID. If none match, a complete valid profile creates a verified trainee automatically. This integration does not post credit to the official CPD ledger.</p></div>
              </section>
              <section v-if="integrationMethod === 'moodle'" id="elms-setup" class="elms-doc-section" aria-labelledby="elms-setup-title">
                <span class="elms-section-number">02 / MOODLE PLUGIN</span><h3 id="elms-setup-title">Install and configure the Moodle plugin</h3>
                <MoodlePluginDownload />
                <ol class="elms-steps">
                  <li><h4>Prepare your provider and courses</h4><p>Use an approved HRD CPD provider and accredited HRD courses owned by that provider. Note each HRD course ID and its corresponding Moodle course ID.</p></li>
                  <li><h4>Register your Moodle source in HRD</h4><p>A system administrator opens <strong>E-learning integrations</strong> in the HRD sidebar and selects <strong>Register Moodle source</strong>. Enter a source name, unique source key, approved CPD provider and Moodle URL. Copy the API key shown after registration and save it securely on the Moodle server.</p><p>Under <strong>Course mappings</strong>, select <strong>Add mapping</strong>, enter each Moodle course ID and choose its accredited HRD course. The selected course must belong to the source’s provider.</p></li>
                  <li><h4>Install and configure the plugin</h4><p>Download the ZIP above. In Moodle, open <strong>Site administration → Plugins → Install plugins</strong>, upload the ZIP and complete the installation or upgrade. Then open <strong>Site administration → Plugins → Local plugins → HRD e-learning integration</strong>.</p><p class="elms-detail">If your host disables ZIP installation, ask your server administrator to extract the <code>hrdintegration</code> folder into <code>local/</code> (or <code>public/local/</code> on Moodle 5.1+) and visit <strong>Site administration → Notifications</strong>. For updates, back up Moodle first and install the newer ZIP through the same process.</p>
                    <div class="elms-table-wrap"><table class="elms-doc-table"><caption>Plugin settings</caption><thead><tr><th scope="col">Setting</th><th scope="col">Value</th></tr></thead><tbody><tr><td>HRD API base URL</td><td><code>{{ hrdApiBase }}</code></td></tr><tr><td>Source API key</td><td>The key supplied by the HRD integration team</td></tr><tr><td>Certificate provider</td><td><strong>Custom certificate</strong> for <code>mod_customcert</code>, or <strong>Manual/test</strong> for controlled tests and imports</td></tr><tr><td>Moodle cron</td><td>Run regularly; completion reconciliation runs every five minutes</td></tr></tbody></table></div>
                  </li>
                  <li><h4>Validate courses and prepare learner profiles</h4><p>Open the plugin dashboard and choose <strong>Test HRD connection</strong>. Add matching course mappings in Moodle, validate them against HRD, then enable them. Learner matching and profile validation happen automatically in HRD.</p><p class="elms-detail">Provide first name, last name, personal email and phone number for new trainees. National ID, employee ID and licence number are optional; configure their Moodle custom profile field shortnames in the plugin settings if used.</p></li>
                  <li><h4>Enable delivery</h4><p>Enable HRD delivery after validating your course mappings. A certificate must belong to the same learner and course and be issued on or after completion. Cron delivers eligible records from Moodle's saved queue.</p></li>
                </ol>
              </section>
              <section v-if="integrationMethod === 'api'" id="elms-api-setup" class="elms-doc-section" aria-labelledby="elms-api-setup-title">
                <span class="elms-section-number">02 / DIRECT API SETUP</span><h3 id="elms-api-setup-title">Prepare your API connection</h3>
                <ol class="elms-steps">
                  <li><h4>Prepare your provider and accredited courses</h4><p>Use an approved HRD CPD provider. Each course you send must map to an accredited HRD course owned by that provider.</p></li>
                  <li><h4>Register a source and map course IDs</h4><p>A system administrator opens <strong>E-learning integrations → Register Moodle source</strong> in HRD. Register the source name, unique source key, provider and Moodle URL. Save the API key shown once on your connector's server.</p><p>Open the source's <strong>Course mappings → Add mapping</strong> and link each Moodle course ID to its accredited HRD course.</p></li>
                  <li><h4>Verify your credential and build the payload</h4><p>Call <code>GET /source</code> using the source's Bearer key to confirm the connection and mappings. Use the endpoints and example in <strong>API reference</strong> to build each completion request. Send the learner’s first name, last name, personal email and phone number, with optional national ID, employee ID or licence number. HRD resolves the trainee automatically.</p><p class="elms-detail">API v1 uses <code>moodleCourseId</code> and <code>learner.moodleUserId</code>. Retain those field names and use positive integer IDs from the registered source. A completion and an issued certificate for the same learner and course are both required.</p></li>
                  <li><h4>Implement delivery and receipt tracking</h4><p>Save each event and its payload before sending it. Keep its event ID stable, store HRD's receipt, and retry temporary failures with increasing delays. Use the receipt lookup endpoint when checking an event's delivery status.</p><p class="elms-detail">Keep the source API key on your server. Send requests from your connector's backend; do not embed the key in a public page or mobile application.</p></li>
                </ol>
              </section>
              <section v-if="integrationMethod === 'api'" id="elms-api" class="elms-doc-section" aria-labelledby="elms-api-title">
                <span class="elms-section-number">03 / API REFERENCE</span><h3 id="elms-api-title">Authenticate and send completions</h3><p>Use the source API key for every request. Build your connector's URLs from the HRD API base URL shown in the overview.</p>
                <div class="elms-code-label"><span>Request headers</span><button class="elms-copy-btn" type="button" aria-label="Copy request headers" @click="copyToClipboard(requestHeaders)"><v-icon small>mdi-content-copy</v-icon>Copy</button></div><pre class="elms-code"><code>{{ requestHeaders }}</code></pre>
                <div class="elms-endpoint"><h4>Check source and course mappings</h4><div class="elms-api"><span class="elms-method get">GET</span><code>{{ integrationApiBase }}/source</code><button class="elms-copy-btn" type="button" aria-label="Copy source endpoint" @click="copyToClipboard(integrationApiBase + '/source')"><v-icon small>mdi-content-copy</v-icon></button></div><p>Verifies the credential and returns active accredited course mappings. This request creates no completion.</p></div>
                <div class="elms-endpoint"><h4>Submit a completion</h4><div class="elms-api"><span class="elms-method post">POST</span><code>{{ integrationApiBase }}/completions</code><button class="elms-copy-btn" type="button" aria-label="Copy completion endpoint" @click="copyToClipboard(integrationApiBase + '/completions')"><v-icon small>mdi-content-copy</v-icon></button></div><p>Send a stable event ID, learner identity, completion date and certificate evidence. Dates use ISO 8601; the example uses UTC. The score and certificate verification URL are optional.</p></div>
                <div class="elms-code-label"><span>Example request body</span><button class="elms-copy-btn" type="button" aria-label="Copy payload" @click="copyToClipboard(payloadExample)"><v-icon small>mdi-content-copy</v-icon>Copy JSON</button></div><pre class="elms-code"><code>{{ payloadExample }}</code></pre>
                <p class="elms-detail">Replace all example IDs and certificate details with your mapped records. HRD selects the accredited course and provider from the registered source mapping.</p>
                <div class="elms-endpoint"><h4>Look up a receipt</h4><div class="elms-api"><span class="elms-method get">GET</span><code>{{ integrationApiBase }}/completions/{eventId}</code><button class="elms-copy-btn" type="button" aria-label="Copy receipt endpoint" @click="copyToClipboard(integrationApiBase + '/completions/{eventId}')"><v-icon small>mdi-content-copy</v-icon></button></div><p>Replace <code>{eventId}</code> with the original event ID. A source can look up only its own receipts.</p></div>
              </section>
              <section id="elms-receipts" class="elms-doc-section" aria-labelledby="elms-receipts-title">
                <span class="elms-section-number">{{ integrationMethod === 'moodle' ? '03' : '04' }} / DELIVERY</span><h3 id="elms-receipts-title">Understand receipts and retries</h3><p>An HTTP success response confirms that HRD stored the evidence. A new valid completion is linked to an existing or newly registered trainee.</p>
                <div class="elms-status-grid"><article><span class="elms-status completed">COMPLETED</span><h4>Linked in HRD</h4><p>The completion is linked to an existing trainee or a newly registered verified trainee.<template v-if="integrationMethod === 'moodle'"> Moodle shows <strong>ACCEPTED</strong> in its delivery records.</template></p></article><article><span class="elms-status review">VALIDATION_ERROR</span><h4>Profile needs correction</h4><p>HRD rejects invalid supplied fields or an incomplete profile for a new trainee with HTTP 400 and field details. Correct the source data before creating a corrected event. Historical identity-review receipts remain unchanged.</p></article></div>
                <div v-if="integrationMethod === 'api'" class="elms-table-wrap"><table class="elms-doc-table"><caption>HTTP responses</caption><thead><tr><th scope="col">Code</th><th scope="col">Meaning / next step</th></tr></thead><tbody><tr><td><code>202</code></td><td>A new completion receipt was stored.</td></tr><tr><td><code>200</code></td><td>An identical event was already received. Reuse its receipt.</td></tr><tr><td><code>400</code></td><td>Correct invalid fields or dates in the request.</td></tr><tr><td><code>401 / 403</code></td><td>Check the source key, source status and provider ownership.</td></tr><tr><td><code>409</code></td><td>Event or completion conflict. An event ID must retain the same payload.</td></tr><tr><td><code>422</code></td><td>Check course mapping and HRD accreditation.</td></tr><tr><td><code>429 / 5xx</code></td><td>Temporary failure. Retry with increasing delays and retain the same event ID and payload.</td></tr></tbody></table></div>
                <div class="elms-callout"><strong>Keep retries consistent</strong><p v-if="integrationMethod === 'moodle'">The plugin retains the same event ID and payload when retrying network failures and temporary server errors. For credential or mapping errors, fix the configuration, then select <strong>Manual retry</strong> or <strong>Retry all failed</strong> in the plugin’s delivery records. Bulk retry includes eligible failures across every page. Retries preserve the saved payload; editing a Moodle profile does not rebuild an existing failed event.</p><p v-else>Your connector must retain the same event ID and payload when retrying network failures, HTTP <code>429</code> and HTTP <code>5xx</code>. Stop and review credential, mapping or payload errors before sending again. Replaying an identical event returns its existing receipt.</p></div>
              </section>
            </div>
          </div>
          <footer class="elms-guide-footer"><span>{{ integrationMethod === 'moodle' ? 'Moodle plugin guide' : 'Direct API guide · API v1' }}</span><span role="status" aria-live="polite">{{ copyMessage }}</span></footer>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import ApiService from "@/common/api.service";
import {settings} from '@/Settings';
import MoodlePluginDownload from '@/components/ELearning/MoodlePluginDownload.vue';

export default {
  name: "LandingPage",
  components: {MoodlePluginDownload},
  data() {
    return {
      brandLogo: require("@/assets/img/MOH_logo.png"),
      mobileNavOpen: false,
      activeSection: "product",
      navLinks: [
        {label: "Insights", target: "insights"},
        {label: "Product", target: "product"},
        {label: "HR roles", target: "hr-roles"},
        {label: "eLMS integrations", target: "elms-guide"},
      ],
      productHighlights: [
        {
          title: "Complete development records",
          body: "Manage graduates, training programs, trainees, and trainers with structured data entry and review.",
        },
        {
          title: "Training oversight",
          body: "Track short and long term training participation, attendance, and certification status in real time.",
        },
      ],
      showElmsGuide: false,
      integrationMethod: 'moodle',
      integrationMethods: [
        {id: 'moodle', label: 'Moodle plugin', description: 'Install the plugin and configure your site', icon: 'mdi-puzzle-outline'},
        {id: 'api', label: 'Direct API', description: 'Build and manage your own connector', icon: 'mdi-code-braces'},
      ],
      activeGuideSection: 'elms-overview',
      copyMessage: '',
      requestHeaders: 'Authorization: Bearer <source-key>.<source-secret>\nContent-Type: application/json',
      payloadExample: `{
  "eventId": "4ec63c13-0cef-4e38-bf22-f76cffea3954",
  "moodleCourseId": 18,
  "completionReference": "moodle-course-completion:846",
  "learner": {
    "moodleUserId": 148,
    "firstName": "Hana",
    "lastName": "Tesfaye",
    "email": "hana@example.org",
    "phoneNumber": "+251911234567"
  },
  "completedAt": "2026-09-17T10:30:00Z",
  "scorePercent": 86,
  "certificate": {
    "reference": "CERT-2026-00481",
    "issuedAt": "2026-09-17T10:35:00Z",
    "verificationUrl": "https://learning.example.org/verify/CERT-2026-00481"
  }
}`,
      publicKpis: [
        {
          title: "Total No. of Health Professionals",
          value: 0,
          color: "#0f172a",
          keys: [
            "totalHealthProfessionals",
            "totalNoOfHealthProfessionals",
            "healthProfessionalsTotal",
            "total_health_professionals",
            "totalHealthProfessional",
          ],
        },
        {
          title: "Trainees Under CPD Activity",
          value: 0,
          color: "#0ea5e9",
          keys: [
            "traineesUnderCpdActivity",
            "traineesUnderCPDActivity",
            "trainees_under_cpd_activity",
            "cpdActivityTrainees",
            "totalTraineesUnderCpdActivity",
          ],
        },
        {
          title: "Total Trainees who took CEU",
          value: 0,
          color: "#22c55e",
          keys: [
            "totalTraineesTookCeu",
            "totalTraineesWhoTookCeu",
            "totalTraineesWithCeu",
            "total_trainees_took_ceu",
            "totalTraineesTookCEU",
          ],
        },
        {
          title: "Total Trainees who failed",
          value: 0,
          color: "#f97316",
          keys: [
            "totalTraineesFailed",
            "totalTraineesWhoFailed",
            "total_trainees_failed",
            "traineesFailed",
          ],
        },
        {
          title: "Total Courses taken by trainees",
          value: 0,
          color: "#6366f1",
          keys: [
            "totalCoursesTakenByTrainees",
            "totalCoursesTaken",
            "total_courses_taken_by_trainees",
            "coursesTakenByTrainees",
          ],
        },
      ],
      clinicians: [
        {
          title: "Ministry HR",
          body: "Monitor national HRD indicators and program performance with consistent data from every level.",
        },
        {
          title: "Regional HR",
          body: "Coordinate trainings and track facility readiness with aligned reporting requirements.",
        },
        {
          title: "Facility HR",
          body: "Maintain accurate trainee and trainer records, certifications, and attendance histories.",
        },
        {
          title: "Leadership",
          body: "View aggregated workforce capacity to inform policy, staffing, and investment decisions.",
        },
      ],
      steps: [
        {
          step: "1",
          title: "Collect",
          body: "HR teams capture training, program, and personnel details in a guided, consistent way.",
        },
        {
          step: "2",
          title: "Organize",
          body: "Records are structured automatically so approvals, attendance, and completions stay clear.",
        },
        {
          step: "3",
          title: "Measure",
          body: "Capacity indicators and training coverage reveal where to invest and how to improve performance.",
        },
      ],
      footerLinks: [
        {label: "Privacy", href: "#"},
        {label: "Terms", href: "#"},
        {label: "Contact", href: "mailto:hello@link.health"},
      ],
    };
  },
  computed: {
    guideSections() {
      return [
        {id: 'elms-overview', label: 'Overview'},
        ...(this.integrationMethod === 'moodle'
          ? [{id: 'elms-setup', label: 'Setup & mappings'}]
          : [{id: 'elms-api-setup', label: 'Source setup'}, {id: 'elms-api', label: 'API reference'}]),
        {id: 'elms-receipts', label: 'Receipts & retries'},
      ];
    },
    hrdApiBase() { return settings.API_SERVER; },
    integrationApiBase() { return this.hrdApiBase + 'integrations/elearning/v1'; },
    currentYear() {
      return new Date().getFullYear();
    },
  },
  watch: {
    activeGuideSection() {
      this.$nextTick(() => {
        const nav = this.$refs.guideNav;
        const active = nav?.querySelector('[aria-current="location"]');
        if (!active || nav.scrollWidth <= nav.clientWidth) return;
        const bounds = nav.getBoundingClientRect(), item = active.getBoundingClientRect();
        if (item.left < bounds.left || item.right > bounds.right) {
          nav.scrollTo({left:nav.scrollLeft + item.left - bounds.left - (nav.clientWidth - item.width) / 2, behavior:'smooth'});
        }
      });
    },
  },
  methods: {
    selectIntegrationMethod(method) {
      if (this.integrationMethod === method) return;
      this.integrationMethod = method;
      this.activeGuideSection = 'elms-overview';
      this.copyMessage = '';
      this.$nextTick(() => {
        if (this.$refs.guideContent) this.$refs.guideContent.scrollTop = 0;
        if (this.$refs.guideNav) this.$refs.guideNav.scrollLeft = 0;
      });
    },
    handleIntegrationMethodKeydown(event, method) {
      const index = this.integrationMethods.findIndex(item => item.id === method);
      let next;
      if (event.key === 'ArrowRight') next = (index + 1) % this.integrationMethods.length;
      else if (event.key === 'ArrowLeft') next = (index + this.integrationMethods.length - 1) % this.integrationMethods.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = this.integrationMethods.length - 1;
      else return;
      event.preventDefault();
      const id = this.integrationMethods[next].id;
      this.selectIntegrationMethod(id);
      this.$nextTick(() => document.getElementById('elms-method-' + id)?.focus());
    },
    formatNumber(value) {
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) {
        return value;
      }
      return numberValue.toLocaleString();
    },
    pickFirstNumber(payload, keys, fallback) {
      if (!payload) {
        return fallback;
      }
      for (const key of keys) {
        const candidate = payload[key];
        if (candidate !== undefined && candidate !== null && candidate !== "") {
          const numberValue = Number(candidate);
          if (!Number.isNaN(numberValue)) {
            return numberValue;
          }
        }
      }
      return fallback;
    },
    async fetchPublicDashboardSummary() {
      try {
        const response = await ApiService.get("/public/dashboard/summary");
        const payload = response && response.data ? (response.data.data || response.data) : {};
        this.publicKpis = this.publicKpis.map((kpi) => ({
          ...kpi,
          value: this.pickFirstNumber(payload, kpi.keys, kpi.value),
        }));
      } catch (error) {
        // Keep fallback values when the public endpoint is unavailable.
      }
    },
    handleNav(target, event) {
      if (target === 'elms-guide') this.openElmsGuide(event);
      else this.scrollTo(target);
    },
    openElmsGuide(event) {
      this.guideTrigger = this.mobileNavOpen ? this.$el.querySelector('.menu-button') : event?.currentTarget;
      this.mobileNavOpen = false;
      this.integrationMethod = 'moodle';
      this.activeGuideSection = 'elms-overview';
      this.copyMessage = '';
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.showElmsGuide = true;
      this.$nextTick(() => this.$refs.guideClose?.focus());
    },
    closeElmsGuide() {
      this.showElmsGuide = false;
      document.body.style.overflow = this.previousBodyOverflow || '';
      this.$nextTick(() => this.guideTrigger?.focus());
    },
    handleGuideKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); this.closeElmsGuide(); return; }
      if (event.key !== 'Tab') return;
      const elements = Array.from(this.$refs.guideDialog.querySelectorAll('button, a[href], summary, [tabindex="0"]')).filter(el => el.tabIndex >= 0 && el.getClientRects().length);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    },
    scrollToGuide(id) {
      this.activeGuideSection = id;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      document.getElementById(id)?.scrollIntoView({behavior, block:'start'});
    },
    syncGuideSection() {
      const content = this.$refs.guideContent;
      if (!content) return;
      const sections = Array.from(content.querySelectorAll('.elms-doc-section'));
      const threshold = content.getBoundingClientRect().top + parseFloat(getComputedStyle(content).paddingTop) + 24;
      const atBottom = content.scrollTop > 0 && content.scrollTop + content.clientHeight >= content.scrollHeight - 2;
      const current = atBottom ? sections[sections.length - 1] : sections.filter(section => section.getBoundingClientRect().top <= threshold).pop() || sections[0];
      if (current) this.activeGuideSection = current.id;
    },
    async copyToClipboard(text) {
      try {
        if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
          this.copyMessage = 'Copied to clipboard.';
          return;
        }
      } catch (error) {
        // Fallback when clipboard API is not available.
      }
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(textarea);
      this.copyMessage = copied ? 'Copied to clipboard.' : 'Select and copy the text manually.';
    },
    scrollTo(target) {
      this.activeSection = target;
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({behavior: "smooth", block: "start"});
      }
    },
    handleMobileNav(target, event) {
      if (target === 'elms-guide') { this.openElmsGuide(event); return; }
      this.mobileNavOpen = false;
      this.$nextTick(() => this.scrollTo(target));
    },
    goToLogin() {
      this.$router.push("/login");
    },
  },
  created() {
    this.fetchPublicDashboardSummary();
  },
  beforeDestroy() {
    if (this.showElmsGuide) document.body.style.overflow = this.previousBodyOverflow || '';
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@600;700&family=Inter:wght@400;500;600&display=swap");

.landing-page {
  --ink: #0f172a;
  --ink-soft: #0b2940;
  --muted: #6b7280;
  --border: #e5e7eb;
  --bg-soft: #f7f9fb;
  --card: #ffffff;
  font-family: "Inter", "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  background: #ffffff;
  min-height: 100vh;
}

.lp-container {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
}

.lp-header {
  position: sticky;
  top: 0;
  z-index: 40;
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.9);
  border-bottom: 1px solid var(--border);
}

.header-inner {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 18px 0;
}

.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  justify-self: start;
}

.brand-mark {
  width: 44px;
  height: 44px;
  object-fit: contain;
}

.brand-name {
  font-family: "Poppins", "Inter", sans-serif;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 14px;
}

.nav-links {
  display: flex;
  gap: 18px;
  align-items: center;
  justify-self: end;
  justify-content: flex-end;
}

.link-button {
  background: none;
  border: none;
  color: var(--muted);
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  border-bottom: 2px solid transparent;
  padding-bottom: 6px;
}

.link-button:hover {
  color: #0f172a;
  transform: translateY(-1px);
}

.link-button.is-active {
  color: var(--ink);
  border-color: var(--ink);
}

.link-button:focus,
.link-button:focus-visible {
  outline: none;
  box-shadow: none;
}

.cta-group {
  display: flex;
  gap: 10px;
  align-items: center;
}

.btn {
  border-radius: 12px;
  border: 1px solid transparent;
  padding: 10px 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn.primary {
  background: #1976d2;
  color: #fff;
  box-shadow: 0 10px 25px rgba(25, 118, 210, 0.25);
}

.btn.primary:hover {
  background: #1565c0;
}

.btn.ghost {
  background: #fff;
  color: var(--ink);
  border-color: rgba(11, 31, 59, 0.16);
}

.btn.ghost:hover {
  background: rgba(11, 31, 59, 0.04);
}

.btn.outline {
  background: transparent;
  color: var(--ink);
  border-color: rgba(11, 31, 59, 0.2);
}

.menu-button {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 10px 9px;
  cursor: pointer;
  justify-self: end;
}

.menu-button span {
  display: block;
  width: 18px;
  height: 2px;
  background: #111827;
}

.mobile-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  justify-content: flex-end;
  z-index: 50;
}

.mobile-sheet {
  width: 90%;
  max-width: 340px;
  background: #fff;
  height: 100%;
  padding: 22px;
  box-shadow: -10px 0 30px rgba(15, 23, 42, 0.15);
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.close-button {
  background: #f3f4f6;
  border: none;
  border-radius: 10px;
  padding: 8px 10px;
  font-size: 14px;
  cursor: pointer;
}

.mobile-links {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.mobile-link {
  background: none;
  border: none;
  text-align: left;
  font-weight: 600;
  color: #111827;
  padding: 10px 0;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
}

.mobile-cta {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lp-section {
  padding: 80px 0;
  border-bottom: 1px solid var(--border);
}

.lp-section.alt {
  background: var(--bg-soft);
}

.hero {
  position: relative;
  background: linear-gradient(180deg, #ffffff 0%, #eef2f7 100%);
  overflow: hidden;
}

.hero::after {
  content: "";
  position: absolute;
  right: -140px;
  top: -160px;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle at center, rgba(15, 23, 42, 0.12), transparent 60%);
  filter: blur(12px);
}

.hero-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 36px;
  align-items: center;
}

.hero-copy {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.eyebrow {
  font-size: 12px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--ink);
  font-weight: 700;
}

.eyebrow.muted {
  color: var(--muted);
}

.hero-title {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 40px;
  line-height: 1.1;
  margin: 0;
}

.text-accent {
  color: var(--ink);
}

.lead {
  font-size: 16px;
  line-height: 1.7;
  color: #4b5563;
  max-width: 640px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.tiny-note {
  font-size: 12px;
  color: #6b7280;
  max-width: 360px;
}

.hero-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 24px;
  box-shadow: 0 14px 40px rgba(15, 23, 42, 0.08);
  padding: 22px;
}

.card-eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: #0b1f3b;
  font-weight: 700;
  font-size: 12px;
  margin-bottom: 14px;
}

.card-list {
  display: grid;
  gap: 12px;
}

.card-list-item {
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 12px;
  background: #fafbfc;
}

.item-title {
  font-weight: 700;
  margin-bottom: 6px;
  color: #0f172a;
}

.item-body {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.6;
}

.section-title {
  font-family: "Space Grotesk", "Manrope", sans-serif;
  font-size: 28px;
  margin: 10px 0;
  color: #0f172a;
}

.section-lead {
  color: #4b5563;
  font-size: 15px;
  line-height: 1.7;
  max-width: 760px;
}

.section-lead.narrow {
  max-width: 640px;
}

.card-grid {
  margin-top: 26px;
  display: grid;
  gap: 16px;
}

.card-grid.three-up {
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.card-grid.four-up {
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
}

.insights-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.elms-modal{position:fixed;inset:0;z-index:200;display:flex;align-items:center;justify-content:center;padding:28px}
.elms-modal__backdrop{position:absolute;inset:0;background:rgba(15,23,42,.55);backdrop-filter:blur(5px)}
.elms-guide{position:relative;display:flex;flex-direction:column;width:min(1120px,100%);height:min(850px,calc(100dvh - 56px));max-height:calc(100vh - 56px);background:#fff;border:1px solid #dce4ed;border-radius:20px;box-shadow:0 30px 90px #0f172a40;overflow:hidden}
.elms-guide-header{display:flex;align-items:flex-start;justify-content:space-between;gap:20px;padding:24px 28px;background:linear-gradient(120deg,#f0f6ff,#fff);border-bottom:1px solid #e2e8f0;flex-shrink:0}
.elms-eyebrow{display:flex;align-items:center;gap:10px;font-size:10px;letter-spacing:1.3px;font-weight:700;color:#64748b}
.elms-eyebrow>span{font-size:10px;letter-spacing:0;background:#dbeafe;color:#1d4ed8;border-radius:5px;padding:3px 6px}
.elms-guide-header h2{font-size:25px;letter-spacing:-.6px;font-weight:700;line-height:1.4;margin:8px 0 4px;color:#0f172a}
.elms-guide-header p{font-size:13px;color:#64748b;line-height:1.6;margin:0}
.elms-close{width:34px;height:34px;display:flex;align-items:center;justify-content:center;background:#fff;border:1px solid #e2e8f0;border-radius:9px;flex-shrink:0;cursor:pointer}
.elms-close:hover{background:#f1f5f9}
.elms-method-picker{padding:16px 28px;border-bottom:1px solid #e2e8f0;flex-shrink:0;background:#fff}
.elms-method-picker>.elms-nav-label{margin-bottom:10px}
.elms-method-tabs{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
.elms-method-tabs>button{display:flex;align-items:center;gap:12px;padding:13px 15px;text-align:left;border:1px solid #dce4ed;border-radius:10px;background:#fff;color:#475569;min-width:0;cursor:pointer}
.elms-method-tabs>button>span{flex:1;min-width:0}.elms-method-tabs strong{display:block;font-size:13px;font-weight:600;line-height:1.5}.elms-method-tabs small{display:block;font-size:11px;line-height:1.6;margin-top:2px;color:#64748b}
.elms-method-tabs .v-icon{color:#64748b;flex-shrink:0}.elms-method-tabs>button:hover{background:#f8fafc;border-color:#94a3b8}
.elms-method-tabs>button[aria-selected=true]{background:#eff6ff;border-color:#3b82f6;color:#1d4ed8}.elms-method-tabs>button[aria-selected=true]>.v-icon{color:#2563eb}
.elms-doc-layout{display:grid;grid-template-columns:220px minmax(0,1fr);min-height:0;flex:1}
.elms-doc-nav{padding:24px 14px;background:#f8fafc;border-right:1px solid #e2e8f0;overflow-y:auto}
.elms-nav-label{display:block;font-size:10px;color:#64748b;font-weight:700;letter-spacing:1px}
.elms-doc-nav>.elms-nav-label{padding:0 12px;margin-bottom:14px}
.elms-doc-nav>button{display:flex;align-items:center;gap:10px;width:100%;padding:11px 12px;text-align:left;color:#475569;font-size:12px;border-radius:8px;margin-bottom:4px;cursor:pointer}
.elms-doc-nav>button>span{font-size:10px;color:#94a3b8}
.elms-doc-nav>button:hover{background:#eaf0f6}
.elms-doc-nav>button.is-current{background:#e5efff;color:#1d4ed8;font-weight:600}
.elms-doc-nav>button.is-current>span{color:#3b82f6}
.elms-nav-note{display:flex;align-items:flex-start;gap:7px;margin:24px 10px 0;padding-top:18px;border-top:1px solid #e2e8f0;color:#64748b;font-size:11px;line-height:1.7}
.elms-doc-content{padding:28px 36px;overflow-y:auto;overscroll-behavior:contain;min-width:0;scroll-padding-top:28px;outline-offset:-3px}
.elms-doc-section{scroll-margin-top:0;padding-bottom:30px;margin-bottom:30px;border-bottom:1px solid #e2e8f0}
.elms-doc-section:last-child{margin-bottom:0;border-bottom:0;padding-bottom:0}
.elms-section-number{display:block;font-size:10px;font-weight:700;letter-spacing:1.2px;color:#3b82f6;margin-bottom:9px}
.elms-doc-section h3{font-size:21px;font-weight:700;letter-spacing:-.4px;line-height:1.4;margin:0 0 12px;color:#0f172a}
.elms-doc-section h4{font-size:14px;line-height:1.6;font-weight:600;color:#1e293b;margin:0 0 5px}
.elms-doc-section p{font-size:13px;color:#475569;line-height:1.85;margin:0 0 12px}
.elms-doc-section code{font-family:'SFMono-Regular',Consolas,monospace;font-size:11px;font-weight:400;color:#1e40af;background:#eff6ff;border-radius:4px;padding:2px 5px;box-shadow:none;overflow-wrap:anywhere}
.elms-flow{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:20px 0;font-size:11px;color:#475569}
.elms-flow>span{display:flex;align-items:center;gap:6px;padding:9px 10px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;font-weight:600}
.elms-flow .v-icon{color:#3b82f6}
.elms-flow>b{color:#94a3b8}
.elms-base-card{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:16px 18px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:10px;margin-bottom:18px}
.elms-base-card>div{min-width:0}
.elms-base-card code{display:block;margin-top:7px;padding:0;background:transparent;color:#0f172a;font-size:13px}
.elms-copy-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;min-width:30px;min-height:30px;padding:5px 8px;border:1px solid #dbe3ed;border-radius:6px;color:#475569;background:#fff;font-size:11px;white-space:nowrap;cursor:pointer;flex-shrink:0}
.elms-copy-btn .v-icon{color:inherit}
.elms-copy-btn:hover{background:#eff6ff;color:#1d4ed8;border-color:#bfdbfe}
.elms-callout{padding:14px 16px;background:#eff6ff;border-left:3px solid #60a5fa;border-radius:0 8px 8px 0}
.elms-callout>strong{display:block;font-size:12px;color:#1e40af;margin-bottom:5px}
.elms-callout p{font-size:12px;margin:0;color:#334155}
.elms-steps{counter-reset:setup;list-style:none;padding:0!important;margin:20px 0 0}
.elms-steps>li{counter-increment:setup;position:relative;padding:0 0 22px 38px}
.elms-steps>li:last-child{padding-bottom:0}
.elms-steps>li:before{content:counter(setup);position:absolute;left:0;top:0;width:25px;height:25px;display:grid;place-items:center;border-radius:8px;background:#eff6ff;color:#2563eb;font-size:11px;font-weight:700}
.elms-detail{font-size:12px!important;color:#64748b!important}
.elms-table-wrap{overflow-x:auto;margin:16px 0 20px;border:1px solid #e2e8f0;border-radius:8px}
.elms-doc-table{width:100%;border-collapse:collapse;text-align:left;font-size:12px;color:#475569;line-height:1.7}
.elms-doc-table caption{text-align:left;padding:10px 14px;background:#f8fafc;font-weight:600;color:#334155;border-bottom:1px solid #e2e8f0}
.elms-doc-table th{font-size:11px;color:#64748b;background:#fbfcfe;font-weight:600}
.elms-doc-table th,.elms-doc-table td{padding:10px 14px;border-bottom:1px solid #edf2f7;vertical-align:top}
.elms-doc-table tr:last-child td{border-bottom:0}
.elms-doc-table td:first-child{width:30%}
.elms-code-label{display:flex;align-items:center;justify-content:space-between;gap:12px;font-size:11px;font-weight:600;color:#64748b;margin:20px 0 8px}
.elms-code{margin:0 0 16px;padding:18px 20px;background:#102135;border:1px solid #1e344b;border-radius:10px;overflow-x:auto;line-height:1.85;tab-size:2}
.elms-code code{color:#dbeafe;background:transparent;padding:0;font-size:11px;white-space:pre;overflow-wrap:normal;box-shadow:none}
.elms-endpoint{margin-top:24px}
.elms-api{display:flex;align-items:center;gap:8px;padding:11px 12px;margin:10px 0;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px}
.elms-api>code{flex:1;min-width:0;background:transparent;padding:0;color:#334155;font-size:11px}
.elms-method{font-size:10px;font-weight:700;padding:4px 6px;border-radius:4px;align-self:flex-start}
.elms-method.get{background:#dcfce7;color:#15803d}
.elms-method.post{background:#dbeafe;color:#1d4ed8}
.elms-status-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;margin:18px 0 22px}
.elms-status-grid article{padding:16px;border:1px solid #e2e8f0;border-radius:10px}
.elms-status{display:inline-block;font-size:9px;font-weight:700;letter-spacing:.3px;padding:4px 6px;border-radius:4px;margin-bottom:10px}
.elms-status.completed{background:#dcfce7;color:#166534}
.elms-status.review{background:#fef3c7;color:#92400e}
.elms-status-grid p{font-size:12px;margin:0}
.elms-guide-footer{display:flex;align-items:center;justify-content:space-between;gap:12px;min-height:43px;padding:10px 24px;border-top:1px solid #e2e8f0;color:#64748b;font-size:10px;flex-shrink:0}
.elms-guide-footer [role=status]{color:#15803d}
.elms-guide button:focus-visible,.elms-guide a:focus-visible{outline:2px solid #2563eb;outline-offset:2px}
.fade-slide-enter-active,.fade-slide-leave-active{transition:opacity .2s ease}
.fade-slide-enter,.fade-slide-leave-to{opacity:0}
@media(max-width:720px){
 .elms-modal{padding:10px}.elms-guide{height:calc(100dvh - 20px);max-height:calc(100dvh - 20px);border-radius:14px}
 .elms-guide-header{padding:18px 16px;gap:10px}.elms-guide-header h2{font-size:22px}.elms-guide-header p{font-size:12px}.elms-eyebrow{font-size:8px;letter-spacing:.8px}
 .elms-method-picker{padding:12px 16px}.elms-method-picker>.elms-nav-label{font-size:9px;margin-bottom:8px}.elms-method-tabs{gap:8px}.elms-method-tabs>button{gap:7px;padding:10px}.elms-method-tabs strong{font-size:12px}.elms-method-tabs small,.elms-method-check{display:none!important}
 .elms-doc-layout{display:flex;flex-direction:column}.elms-doc-nav{display:flex;flex-shrink:0;gap:4px;padding:9px 10px;overflow-x:auto;overflow-y:hidden;border-right:0;border-bottom:1px solid #e2e8f0}
 .elms-doc-nav>.elms-nav-label,.elms-nav-note{display:none}.elms-doc-nav>button{width:auto;white-space:nowrap;padding:8px 10px;margin:0;font-size:11px}.elms-doc-nav>button>span{display:none}
 .elms-doc-content{padding:22px 18px;scroll-padding-top:22px}.elms-doc-section h3{font-size:19px}.elms-flow{gap:6px}.elms-flow>span{padding:7px;font-size:10px}.elms-base-card{padding:12px;gap:8px}.elms-base-card code{font-size:11px}
 .elms-steps>li{padding-left:31px}.elms-doc-table th,.elms-doc-table td{padding:9px}.elms-code{padding:14px}.elms-api{padding:9px;gap:6px}.elms-api .elms-copy-btn{min-width:26px;padding:4px}.elms-status-grid{grid-template-columns:1fr}.elms-guide-footer{padding:10px 14px;font-size:9px}
}

.insights-pill {
  background: rgba(15, 23, 42, 0.08);
  color: #0f172a;
  border-radius: 999px;
  padding: 8px 14px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.insight-grid {
  margin-top: 18px;
}

.insight-wrap {
  display: grid;
  gap: 16px;
}

.insight-hero {
  background: linear-gradient(135deg, #0f172a, #0b2940);
  color: #e5e7eb;
  border-radius: 18px;
  padding: 18px;
  display: grid;
  gap: 12px;
  box-shadow: 0 14px 38px rgba(0, 0, 0, 0.16);
}

.insight-hero-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.pill-dark {
  background: rgba(255, 255, 255, 0.12);
  color: #e5e7eb;
  border-radius: 999px;
  padding: 6px 12px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.insight-hero-body {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
}

.insight-hero-value {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 36px;
  font-weight: 700;
  color: #ffffff;
}

.insight-hero-delta {
  color: #a5f3fc;
  font-size: 14px;
}

.insight-hero-delta.positive {
  color: #22c55e;
}

.muted-text {
  color: rgba(229, 231, 235, 0.8);
  font-size: 13px;
}

.insight-chart {
  flex: 1;
  min-width: 220px;
  display: grid;
  grid-auto-flow: column;
  align-items: end;
  gap: 6px;
  height: 120px;
}

.insight-chart .chart-bar {
  width: 16px;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.5));
}

.insight-mini-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 12px;
}

.public-kpi-grid {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

@media (max-width: 1200px) {
  .public-kpi-grid {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  }
}

.mini-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 12px;
  display: grid;
  gap: 6px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.mini-top {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #1f2937;
}

.mini-title {
  font-size: 13px;
}

.mini-value {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #0f172a;
}

.public-kpi-card .mini-title {
  line-height: 1.35;
  font-size: 14px;
}

.public-kpi-card .mini-value {
  font-size: 26px;
}

.public-kpi-card {
  min-height: 140px;
  padding: 16px;
  text-align: center;
  align-items: center;
}

.public-kpi-card .mini-top {
  justify-content: center;
}

.mini-delta {
  font-size: 12px;
  color: #6b7280;
}

.mini-delta.positive {
  color: #0f9d58;
}

.insight-card {
  gap: 10px;
}

.insight-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: #1f2937;
}

.insight-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.insight-value {
  font-family: "Poppins", "Inter", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
}

.insight-delta {
  font-size: 13px;
  color: #6b7280;
}

.insight-delta.positive {
  color: #0f9d58;
}

.spark-bars {
  display: grid;
  grid-auto-flow: column;
  align-items: end;
  gap: 4px;
  height: 52px;
  margin-top: 6px;
}

.spark-bar {
  width: 10px;
  border-radius: 6px 6px 0 0;
  opacity: 0.9;
}

.info-card {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.05);
  display: grid;
  gap: 8px;
}

.info-card h3 {
  margin: 0;
  font-size: 17px;
  color: #0f172a;
}

.info-card p {
  margin: 0;
  color: #4b5563;
  line-height: 1.6;
  font-size: 14px;
}

.focus-card {
  position: relative;
  padding-top: 22px;
}

.focus-card .color-bar {
  width: 80px;
  height: 6px;
  border-radius: 999px;
  position: absolute;
  top: 10px;
  left: 18px;
}

.step-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 12px;
  align-items: start;
}

.step-index {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  background: var(--ink);
  color: #fff;
  font-weight: 700;
  display: grid;
  place-items: center;
}

.timeline {
  margin-top: 26px;
  display: grid;
  gap: 12px;
}

.timeline-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.timeline-caption {
  color: #4b5563;
  font-size: 13px;
}

.timeline-steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}

.timeline-step {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: start;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.timeline-badge {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: var(--ink);
  color: #fff;
  font-weight: 700;
  display: grid;
  place-items: center;
  font-size: 14px;
}

.timeline-title {
  font-weight: 700;
  color: #0f172a;
}

.timeline-text {
  color: #4b5563;
  font-size: 13px;
  line-height: 1.5;
}

.lp-footer {
  background: #ffffff;
  border-top: 1px solid var(--border);
  padding: 24px 0;
  font-size: 13px;
  color: #6b7280;
}

.footer-content {
  display: grid;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}

.footer-meta {
  display: grid;
  gap: 6px;
}

.footer-subtitle {
  color: #4b5563;
}

.footer-links {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.footer-link a {
  color: #0f172a;
  text-decoration: none;
  font-weight: 600;
}

.footer-link a:hover {
  color: var(--ink);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media (max-width: 1024px) {
  .nav-links,
  .cta-group {
    display: none;
  }

  .menu-button {
    display: inline-flex;
  }

  .header-inner {
    padding: 14px 0;
  }
}

@media (max-width: 720px) {
  .lp-section {
    padding: 60px 0;
  }

  .hero-title {
    font-size: 32px;
  }

  .hero-card {
    border-radius: 18px;
  }
}
</style>
